'use client';

import { DialogDisclosure } from '@ariakit/react';
import cx from 'classnames';

import FilterIcon from '~/ui/assets/icons/filter.svg';
import { Button, ButtonSize, ButtonVariant } from '~/ui/components/button';
import { FilterPills } from '~/ui/components/filters/pills';
import { Column, Grid } from '~/ui/components/grid';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Breakpoint } from '~/ui/styles/grid';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { type Filter, type FilterOption, getSelectedFilterOptions } from '~/ui/systems/filter';

import styles from './filterBar.module.scss';

export interface FilterBarProps {
  query?: string;
  eyebrow?: string;
  title: string;
  filters: Filter[];
  isSticky: boolean;
  onButtonClick?: () => void;
  ref?: React.Ref<HTMLDivElement>;
}

export function FilterBar({
  query,
  eyebrow,
  title,
  filters,
  isSticky,
  onButtonClick,
  ref,
}: FilterBarProps): React.ReactElement {
  const bp = useBreakpoint();

  const selectedFilterOptions: Record<string, FilterOption[]> = filters
    .filter(filter => filter.includeInPills)
    .reduce(
      (acc, filter) => ({ ...acc, ...{ [filter.name]: getSelectedFilterOptions(filter) } }),
      {},
    );
  const selectedFilterOptionsCount = Object.values(selectedFilterOptions).flatMap(
    options => options,
  ).length;
  const pillsLabel = selectedFilterOptionsCount ? `(${selectedFilterOptionsCount})` : '';
  const actionLabel = isSticky
    ? bp === Breakpoint.MOBILE
      ? pillsLabel
      : `${title || 'Filter'} ${pillsLabel}`
    : title || 'Filter';

  return (
    <>
      <div
        className={cx(styles.bar, {
          [styles.isSticky]: isSticky,
        })}
        ref={ref}
      >
        <Grid>
          <Column
            span={{ [Breakpoint.MOBILE]: 3, [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 5 }}
            offsetLeft={{ [Breakpoint.DESKTOP]: 1 }}
            className={cx(styles.eyebrow, {
              [styles.isSticky]: isSticky,
            })}
          >
            {isSticky && (
              <div>
                <p className={typographyStyles.paragraph}>{eyebrow}</p>
                {query && (
                  <p className={cx(typographyStyles.paragraph, styles.eyebrowText)}>
                    &lsquo;{query}&rsquo;
                  </p>
                )}
              </div>
            )}
          </Column>

          <Column
            span={{ [Breakpoint.MOBILE]: 2, [Breakpoint.TABLET]: 2, [Breakpoint.DESKTOP]: 2 }}
            className={styles.buttonContainer}
          >
            <DialogDisclosure
              render={
                <Button
                  className={cx(styles.button, {
                    [styles.round]: !actionLabel,
                  })}
                  variant={ButtonVariant.SECONDARY}
                  size={ButtonSize.TINY}
                  aria-label={actionLabel}
                  onClick={() => onButtonClick && onButtonClick()}
                >
                  {!!actionLabel && <span>{actionLabel}</span>}
                  <FilterIcon className={styles.icon} />
                </Button>
              }
            />
          </Column>
        </Grid>
      </div>

      <Grid className={styles.pills}>
        <Column
          span={{ [Breakpoint.TABLET]: 5, [Breakpoint.DESKTOP]: 5 }}
          offsetLeft={{ [Breakpoint.DESKTOP]: 1 }}
        >
          {bp !== Breakpoint.MOBILE && (
            <FilterPills selectedFilterOptions={selectedFilterOptions} />
          )}
        </Column>
      </Grid>
    </>
  );
}
